import React from 'react';
import PublicLayout, { PublicHero, PublicButton, PUBLIC_CONTACT_EMAIL } from '../../components/layout/PublicLayout';
import { motion } from 'framer-motion';
import { FileText, FolderTree, Lock, Smartphone, LifeBuoy, ShieldCheck } from 'lucide-react';

const caps = [
  { icon: FileText, title: 'Read & review documents', desc: 'A clean, focused reading experience for legal documents.' },
  { icon: FolderTree, title: 'Organise your material', desc: 'Keep documents and related material structured and easy to find.' },
  { icon: Lock, title: 'Privacy-oriented', desc: 'Built with security and access control as core principles.' },
  { icon: Smartphone, title: 'Mobile & tablet', desc: 'Designed for use on mobile and tablet devices.' },
];

const Lios = () => (
  <PublicLayout title="LIOS" description="LIOS by VSKR Legal — intelligent document management for legal professionals. Currently in testing.">
    <PublicHero eyebrow="Product · In Testing" title={<>LIOS — your intelligent <span className="text-[#2D8CFF]">document workspace</span></>}
      subtitle="LIOS helps legal professionals read, organise and work with documents in one focused, privacy-oriented workspace. LIOS is currently available for testing as we prepare it for wider release.">
      <PublicButton to="/help" testid="lios-support">Get Support</PublicButton>
      <PublicButton to="/privacy" variant="secondary" testid="lios-privacy">Privacy</PublicButton>
    </PublicHero>

    <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-12">
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {caps.map((c, i) => (
          <motion.div key={c.title} initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
            className="glass-card p-6 rounded-2xl">
            <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4 bg-[#2D8CFF]/15 text-[#2D8CFF]"><c.icon className="w-5 h-5" /></div>
            <h3 className="font-semibold mb-1.5">{c.title}</h3>
            <p className="text-sm opacity-70 leading-relaxed">{c.desc}</p>
          </motion.div>
        ))}
      </div>
    </section>

    <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-20">
      <div className="glass-card rounded-2xl p-7 lg:p-10 grid gap-6 md:grid-cols-2">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-[#2D8CFF]" /> Availability & testing</h2>
          <p className="text-sm opacity-75 mt-2 leading-relaxed">
            LIOS is currently in a testing stage. A public download link will appear here once LIOS is available on
            app stores. During testing, access is provided to invited participants.
          </p>
          {/* Store link intentionally disabled until a real URL exists */}
          <button disabled className="mt-4 px-5 py-2.5 rounded-full text-sm font-semibold opacity-50 cursor-not-allowed border border-current" data-testid="lios-store-placeholder">
            Coming soon to app stores
          </button>
        </div>
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2"><LifeBuoy className="w-5 h-5 text-[#2D8CFF]" /> Help, privacy & your data</h2>
          <p className="text-sm opacity-75 mt-2 leading-relaxed">
            For support, privacy information, terms, or to request account and data deletion, use the links below or
            email us at <a href={`mailto:${PUBLIC_CONTACT_EMAIL}`} className="text-[#2D8CFF] hover:underline">{PUBLIC_CONTACT_EMAIL}</a>.
          </p>
          <div className="flex flex-wrap gap-3 mt-4">
            <PublicButton to="/help" variant="secondary" testid="lios-help">Help</PublicButton>
            <PublicButton to="/account-data-deletion" variant="secondary" testid="lios-deletion">Account & Data Deletion</PublicButton>
          </div>
        </div>
      </div>
    </section>
  </PublicLayout>
);

export default Lios;
